import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Button from "components/common/Button";
import PlgSignupLayout from "plg/screens/Signup/PlgSignupLayout";
import FieldRenderer, { groupFieldsIntoRows } from "plg/screens/Signup/FieldRenderer";
import usePlgSignupConfig, { resetSignupConfigCache } from "plg/screens/Signup/usePlgSignupConfig";

import {
  answeredTools,
  buildStepDefaults,
  buildStepSchema,
  getNextStepPath,
  getPrevStepPath,
  isStepShown,
  visibleSteps,
} from "plg/utils/configSchema";
import {
  clearSignupSession,
  loadSignupSession,
  saveSignupSession,
  signupTokenName,
  updateSignupAnswers,
  PLG_SIGNUP_SESSION_KEY,
} from "plg/utils/signupSession";
import type { SignupSession } from "plg/utils/signupSession";
import type { PlgSignupConfig, PlgStep, SignupAnswers } from "plg/types";
import { PLG_SIGNUP_ROUTE } from "constants/routes";
import { hasPlgSession } from "plg/auth/plgTokenStore";
import { fetchSetupProgress, saveSetupStep } from "plg/api/plgSetup";
import { createAccountFromFirstStep } from "plg/screens/Signup/createAccount";
import { HIRE_ROUTES } from "plg/routes";
import useStepTracking from "plg/analytics/useStepTracking";
import { fieldForAnswers, fillAnswers } from "plg/utils/stepCopy";

interface StepFormProps {
  step: PlgStep;
  config: PlgSignupConfig;
  session: Pick<SignupSession, "prefill" | "enriched" | "answers" | "signupToken">;
  /**
   * Where the first step gets its sign-up token when the session has none yet (the magic-link landing
   * verifies the link on Continue). null = it could not, and has shown why.
   */
  getSignupToken?: () => Promise<string | null>;
}

const SAVE_FAILED = "We could not save this step. Please try again.";

export function StepForm({ step, config, session, getSignupToken }: StepFormProps) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const schema = useMemo(() => buildStepSchema(step, config), [step, config]);
  // Google / Microsoft's name prefills First and Last name; saved answers (Back, resume) come first.
  const defaults = useMemo(
    () => buildStepDefaults(step, session.prefill, session.answers, session.signupToken ? signupTokenName(session.signupToken) : {}),
    [step, session]
  );
  const methods = useForm<SignupAnswers>({ resolver: yupResolver(schema) as any, defaultValues: defaults });
  const enriched = !!session.enriched;
  const industry = typeof session.answers.industry === "string" ? session.answers.industry : undefined;
  const trackCompleted = useStepTracking(step, config, industry);

  // Signed in (the account exists): save this step. Signed out: this is the first step, which creates the account.
  const saveStep = async (values: SignupAnswers): Promise<boolean> => {
    if (hasPlgSession()) {
      const res = await saveSetupStep(step.key, values);
      if (res.ok === true) {
        updateSignupAnswers(values);
        return true;
      }
      if (res.code === "SETUP_ALREADY_COMPLETE") {
        clearSignupSession();
        navigate(HIRE_ROUTES.home, { replace: true });
      } else setFormError(SAVE_FAILED);
      return false;
    }
    const signupToken = session.signupToken || (getSignupToken ? await getSignupToken() : null);
    if (!signupToken) return false;
    const result = await createAccountFromFirstStep({ signupToken, prefill: session.prefill, enriched: session.enriched }, values);
    if (result.ok === true) return true;
    if ("redirect" in result) navigate(result.redirect, { replace: true });
    else setFormError(result.message);
    return false;
  };

  const goOn = async (values: SignupAnswers, skipped: boolean) => {
    if (busy) return;
    setBusy(true);
    setFormError(null);
    try {
      if (!(await saveStep(values))) return;
      trackCompleted(values, skipped);
      // The next step can depend on these answers (Epic or Cerner picked here adds a follow-up step).
      navigate(getNextStepPath(config, step.key, { ...session.answers, ...values }));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (values: SignupAnswers) => goOn(values, false);

  const onSkip = () => {
    const empty: SignupAnswers = {};
    step.fields.forEach((f) => {
      empty[f.key] = f.type === "toolPicker" || f.type === "multiSelect" ? [] : "";
    });
    goOn(empty, true);
  };

  // "Step 2 of 3": counted over the steps these answers ask, live, so picking Epic on the tools step adds one.
  const watched = methods.watch();
  const answers = { ...session.answers, ...watched };
  // Copy can name earlier answers ("What apps do you use at {companyName|your business}") and follow them (labelBy).
  const title = fillAnswers((enriched && step.titleEnriched) || step.title, answers);
  const subtitleText = (enriched && step.subtitleEnriched) || step.subtitle;
  const subtitle = subtitleText && fillAnswers(subtitleText, answers);
  const cta = (enriched && step.ctaEnriched) || step.cta || "Continue";
  const asked = visibleSteps(config, answers);
  const stepNo = asked.findIndex((s) => s.key === step.key) + 1;
  const backPath = step.backLabel && !getSignupToken ? getPrevStepPath(config, step.key, session.answers) : null;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        {stepNo > 0 && <p className="plg-signup__step">{`Step ${stepNo} of ${asked.length}`}</p>}
        <h1 className="plg-signup__title">{title}</h1>
        {subtitle && <p className="plg-signup__sub">{subtitle}</p>}
        {groupFieldsIntoRows(step.fields).map((row) => (
          <div key={row.map((f) => f.key).join("-")} className={row.length > 1 ? "plg-field-row" : "plg-field-group"}>
            {row.map((field) => (
              <FieldRenderer
                key={field.key}
                field={fieldForAnswers(field, answers)}
                config={config}
                enriched={enriched}
                industry={industry}
                optionalTag={!step.hideOptionalTags}
              />
            ))}
          </div>
        ))}
        {formError && (
          <div className="plg-signup__note" role="alert">
            {formError}
          </div>
        )}
        <div className={step.skippable || backPath ? "plg-step-actions plg-step-actions--split" : "plg-step-actions"}>
          {backPath && (
            // Back keeps what was typed here, so the answers are still there on the way forward again.
            <Button
              type="button"
              variant="outlined"
              size="large"
              label={step.backLabel}
              onClick={() => {
                updateSignupAnswers(methods.getValues());
                navigate(backPath);
              }}
              isFullWidth
            />
          )}
          {step.skippable && !getSignupToken && (
            <Button
              type="button"
              variant="outlined"
              size="large"
              label={step.skipLabel || "Not now"}
              onClick={onSkip}
              isFullWidth
              isDisabled={busy}
            />
          )}
          <Button type="submit" variant="contained" size="large" label={cta} onClick={undefined} isFullWidth isLoading={busy} isDisabled={busy} />
        </div>
      </form>
    </FormProvider>
  );
}

function PlgStepPage() {
  const navigate = useNavigate();
  const { stepKey } = useParams<{ stepKey: string }>();
  const { config, error } = usePlgSignupConfig();
  // The account exists from the first step on: a signed-in customer's answers come from the server.
  const signedIn = hasPlgSession();
  const [progressLoaded, setProgressLoaded] = useState(0);
  const [progressFailed, setProgressFailed] = useState(false);
  // Read "was there a session at all?" BEFORE loadSignupSession(), which clears an expired one.
  const { session, hadSession } = useMemo(() => {
    const had = !!sessionStorage.getItem(PLG_SIGNUP_SESSION_KEY);
    return { session: loadSignupSession(), hadSession: had };
  }, [stepKey, progressLoaded]);
  const step = config?.steps.find((s) => s.key === stepKey);
  const firstStepKey = config?.steps[0]?.key;

  // Resuming setup after a sign-in (or in a new tab): load the saved answers once.
  useEffect(() => {
    if (!signedIn || session) return;
    fetchSetupProgress().then((res) => {
      if (res.ok === false) {
        setProgressFailed(true);
        return;
      }
      if (res.data.setupComplete) {
        navigate(HIRE_ROUTES.home, { replace: true });
        return;
      }
      saveSignupSession({ prefill: null, enriched: false, answers: res.data.answers });
      setProgressLoaded((n) => n + 1);
    });
  }, [signedIn, session]);

  useEffect(() => {
    if (!session) {
      if (!signedIn) navigate(hadSession ? `${PLG_SIGNUP_ROUTE}?expired=1` : PLG_SIGNUP_ROUTE, { replace: true });
      return;
    }
    if (!config) return;
    if (!step) {
      // Not a step (e.g. /signup/continue): a signed-in customer resumes after the step that created the account.
      navigate(getNextStepPath(config, signedIn ? firstStepKey : undefined, session.answers), { replace: true });
    } else if (!signedIn && step.key !== firstStepKey) {
      // Before the account exists only the first step is open; it creates the account.
      navigate(getNextStepPath(config), { replace: true });
    } else if (!isStepShown(step, answeredTools(config, session.answers))) {
      // A step these answers do not ask (e.g. the Epic follow-up after Epic was removed): go on past it.
      navigate(getNextStepPath(config, step.key, session.answers), { replace: true });
    }
  }, [session, config, step]);

  if (error || progressFailed) {
    return (
      <PlgSignupLayout>
        <h1 className="plg-signup__title">We could not load sign-up</h1>
        <p className="plg-signup__sub">Please try again in a moment.</p>
        <div className="plg-step-actions">
          <Button
            variant="contained"
            size="large"
            label="Try again"
            isFullWidth
            onClick={() => {
              resetSignupConfigCache();
              window.location.reload();
            }}
          />
        </div>
      </PlgSignupLayout>
    );
  }

  if (!session || !config || !step || !isStepShown(step, answeredTools(config, session.answers))) return null;
  if (!signedIn && step.key !== firstStepKey) return null;

  return (
    <PlgSignupLayout width="md">
      <StepForm key={step.key} step={step} config={config} session={session} />
    </PlgSignupLayout>
  );
}

export default PlgStepPage;
